import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { fetchProductById, restoreProduct, archiveProduct, updateProductField, updateVariantField, createStockProduct, createProductVariant, retireProductVariant, uploadProductImage, uploadProductGallery, removeProductGalleryImage, fetchProductAttributes, updateVariantAttributes, uploadVariantImages, removeVariantImage, fetchProductCustomFields, fetchLanguages, updateProductTranslation, uploadProductCustomFieldFile, fetchCategories, fetchBrands, fetchUnits, fetchTaxes } from '../api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Switch,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Build a URL-safe slug from product name
const slugify = (value = '') => {
  return value
    .toString()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
};

const ProductEdit = () => {
  const { id } = useParams();
  const location = useLocation();
  const isCreateMode = !id;
  const isArchived = new URLSearchParams(location.search).get('archived') === '1';
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadingCustomField, setUploadingCustomField] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filter options
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [originalTranslations, setOriginalTranslations] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    brand_id: '',
    barcode: '',
    hsn: '',
    tax_id: '',
    status: 1,
    type: '',
    indicator: 0,
    is_unlimited_stock: 0,
    tax_included_in_price: 0,
    cod_allowed: 0,
    cancelable_status: 0,
    return_status: 0,
    return_days: 0,
    require_prescription: 0,
    total_allowed_quantity: 0,
    till_status: '',
    is_approved: 0,
    manufacturer: '',
    made_in: '',
    fssai_lic_no: '',
    tags: '',
    slug: '',
    meta_title: '',
    meta_keywords: '',
    meta_description: '',
    schema_markup: '',
    description: '',
    custom_field_values: {}
  });
  const [originalFormData, setOriginalFormData] = useState(null);

  // Variants state
  const [variants, setVariants] = useState([]);
  const [originalVariants, setOriginalVariants] = useState([]);

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link'],
      ['blockquote', 'code-block'],
      [{ indent: '-1' }, { indent: '+1' }],
      ['clean']
    ]
  }), []);

  const loadFilterOptions = useCallback(async () => {
    try {
      const [catRes, brandRes, unitRes, taxRes, customFieldRes, languageRes] = await Promise.all([
        fetchCategories(),
        fetchBrands(),
        fetchUnits(),
        fetchTaxes(),
        fetchProductCustomFields(),
        fetchLanguages()
      ]);
      
      if (Array.isArray(catRes)) setCategories(catRes);
      if (brandRes?.success && Array.isArray(brandRes.data)) setBrands(brandRes.data);

      // Support both plain array and { success, data } response shapes for units
      if (Array.isArray(unitRes)) {
        setUnits(unitRes);
      } else if (unitRes?.success && Array.isArray(unitRes.data)) {
        setUnits(unitRes.data);
      }

      if (taxRes?.success && Array.isArray(taxRes.data)) setTaxes(taxRes.data);
      if (customFieldRes?.success && Array.isArray(customFieldRes.data)) setCustomFields(customFieldRes.data);
      if (languageRes?.success && Array.isArray(languageRes.data)) setLanguages(languageRes.data);
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  }, []);

  const loadProduct = useCallback(async () => {
    setLoading(true);
    setError('');
    if (!id) {
      setProduct(null);
      setGallery([]);
      setVariants([]);
      setOriginalVariants([]);
      setOriginalFormData(null);
      setLoading(false);
      return;
    }
    try {
      const response = await fetchProductById(id, isArchived);
      if (response && response.ok && response.data) {
        const productData = response.data;
        setProduct(productData);
        setGallery(Array.isArray(productData.gallery) ? productData.gallery : []);
        
        // Set form data
        const baseName = productData.name || productData.product_name || '';
        const newFormData = {
          name: baseName,
          category_id: productData.category_id || '',
          brand_id: productData.brand_id || '',
          barcode: productData.barcode || '',
          hsn: productData.hsn || '',
          tax_id: productData.tax_id || '',
          status: productData.status !== undefined ? productData.status : 1,
          type: productData.type === 'packet' ? 'Packet' : productData.type === 'loose' ? 'Loose' : productData.type === 'attribute' ? 'Attribute' : (productData.type || ''),
          indicator: productData.indicator || 0,
          is_unlimited_stock: productData.is_unlimited_stock || 0,
          tax_included_in_price: productData.tax_included_in_price || 0,
          cod_allowed: productData.cod_allowed || 0,
          cancelable_status: productData.cancelable_status || 0,
          return_status: productData.return_status || 0,
          return_days: productData.return_days || 0,
          require_prescription: productData.require_prescription || 0,
          total_allowed_quantity: productData.total_allowed_quantity || 0,
          till_status: productData.till_status || '',
          is_approved: productData.is_approved || 0,
          manufacturer: productData.manufacturer || '',
          made_in: productData.made_in || '',
          fssai_lic_no: productData.fssai_lic_no || '',
          tags: productData.tags || '',
          slug: slugify(baseName),
          meta_title: productData.meta_title || baseName,
          meta_keywords: productData.meta_keywords || '',
          meta_description: productData.meta_description || '',
          schema_markup: productData.schema_markup || '',
          description: productData.description || '',
          custom_field_values: productData.custom_field_values || {}
        };
        setFormData(newFormData);
        setOriginalFormData(JSON.parse(JSON.stringify(newFormData)));
        const translationData = productData.translations || [];
        setTranslations(translationData);
        setOriginalTranslations(JSON.parse(JSON.stringify(translationData)));

        // Set variants
        if (productData.variants && Array.isArray(productData.variants)) {
          const variantsData = productData.variants.map(v => ({
            id: v.id,
            type: v.type || '',
            measurement: v.measurement || '',
            stock_unit_id: v.stock_unit_id || v.unit_id || '',
            price: v.price || 0,
            purchase_price: v.purchase_price || 0,
            discounted_price: v.discounted_price || 0,
            stock: v.stock || 0,
            status: v.status !== undefined ? v.status : 1,
            product_short_name: v.product_short_name || '',
            attribute_value_ids: (v.attribute_values || []).map(attributeValue => attributeValue.id),
            images: v.images || []
          }));
          setVariants(variantsData);
          setOriginalVariants(JSON.parse(JSON.stringify(variantsData)));
        }
      } else {
        setError('Product not found');
      }
    } catch (err) {
      console.error('Error loading product:', err);
      setError('Failed to load product details');
    } finally {
      setLoading(false);
    }
  }, [id, isArchived]);

  useEffect(() => {
    loadFilterOptions();
    loadProduct();
  }, [id, loadFilterOptions, loadProduct]);

  useEffect(() => {
    const loadAttributes = async () => {
      try {
        const response = await fetchProductAttributes(formData.category_id);
        setAttributeOptions(response?.data || []);
      } catch (err) {
        console.error('Error loading product attributes:', err);
        setAttributeOptions([]);
      }
    };
    loadAttributes();
  }, [formData.category_id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'name') {
      const nextName = value;
      setFormData(prev => ({
        ...prev,
        name: nextName,
        slug: slugify(nextName),
        meta_title: nextName
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleVariantChange = (index, field, value) => {
    setVariants(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      custom_field_values: { ...prev.custom_field_values, [fieldId]: value }
    }));
  };

  const handleCustomFieldFileUpload = async (fieldId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingCustomField(fieldId);
    try {
      const response = await uploadProductCustomFieldFile(id, fieldId, file);
      handleCustomFieldChange(fieldId, response.url);
      setSuccess('Custom field file uploaded successfully.');
    } catch (err) {
      console.error('Error uploading custom field file:', err);
      setError(err.response?.data?.error || 'Failed to upload custom field file.');
    } finally {
      setUploadingCustomField(null);
      event.target.value = '';
    }
  };

  const translationForLanguage = (languageId) => translations.find(item => Number(item.language_id) === Number(languageId)) || { language_id: languageId };

  const handleTranslationChange = (languageId, field, value) => {
    setTranslations(current => {
      const existing = current.find(item => Number(item.language_id) === Number(languageId));
      if (existing) return current.map(item => Number(item.language_id) === Number(languageId) ? { ...item, [field]: value } : item);
      return [...current, { language_id: languageId, [field]: value }];
    });
  };

  const handleAddVariant = () => {
    setVariants(prev => [...prev, {
      isNew: true,
      type: formData.type === 'Loose' ? 'loose' : formData.type === 'Attribute' ? 'attribute' : 'packet',
      measurement: 1,
      stock_unit_id: '',
      price: 0,
      purchase_price: 0,
      discounted_price: 0,
      stock: 0,
      status: 1,
      product_short_name: ''
    }]);
  };

  const handleRetireVariant = async (index) => {
    const variant = variants[index];
    if (variant.isNew) {
      setVariants(prev => prev.filter((_, currentIndex) => currentIndex !== index));
      return;
    }

    if (!window.confirm('Retire this variant? It will no longer be available for sale, but invoice and order history will be preserved.')) {
      return;
    }

    try {
      await retireProductVariant(variant.id);
      setVariants(prev => prev.filter((_, currentIndex) => currentIndex !== index));
      setOriginalVariants(prev => prev.filter(originalVariant => originalVariant.id !== variant.id));
      setSuccess('Variant retired successfully.');
    } catch (err) {
      console.error('Error retiring variant:', err);
      setError(err.response?.data?.error || 'Failed to retire variant.');
    }
  };

  const handleImageUpload = async (event) => {
    const image = event.target.files?.[0];
    if (!image) return;

    setUploadingImage(true);
    setError('');
    try {
      const response = await uploadProductImage(id, image);
      setProduct(current => ({ ...current, image: response.image }));
      setSuccess('Product image uploaded successfully.');
    } catch (err) {
      console.error('Error uploading product image:', err);
      setError(err.response?.data?.error || 'Failed to upload product image.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const handleGalleryUpload = async (event) => {
    const images = Array.from(event.target.files || []);
    if (!images.length) return;

    setUploadingGallery(true);
    setError('');
    try {
      const response = await uploadProductGallery(id, images);
      setGallery(current => [...current, ...(response.gallery || [])]);
      setSuccess('Gallery images uploaded successfully.');
    } catch (err) {
      console.error('Error uploading product gallery:', err);
      setError(err.response?.data?.error || 'Failed to upload gallery images.');
    } finally {
      setUploadingGallery(false);
      event.target.value = '';
    }
  };

  const handleGalleryRemove = async (imageId) => {
    try {
      await removeProductGalleryImage(id, imageId);
      setGallery(current => current.filter(image => image.id !== imageId));
      setSuccess('Gallery image removed successfully.');
    } catch (err) {
      console.error('Error removing product gallery image:', err);
      setError(err.response?.data?.error || 'Failed to remove gallery image.');
    }
  };

  const handleVariantImageUpload = async (index, event) => {
    const images = Array.from(event.target.files || []);
    const variant = variants[index];
    if (!images.length || variant.isNew) return;

    try {
      const response = await uploadVariantImages(variant.id, images);
      setVariants(current => current.map((item, currentIndex) => currentIndex === index
        ? { ...item, images: [...(item.images || []), ...(response.images || [])] }
        : item));
      setSuccess('Variant images uploaded successfully.');
    } catch (err) {
      console.error('Error uploading variant images:', err);
      setError(err.response?.data?.error || 'Failed to upload variant images.');
    } finally {
      event.target.value = '';
    }
  };

  const handleVariantImageRemove = async (index, imageId) => {
    const variant = variants[index];
    try {
      await removeVariantImage(variant.id, imageId);
      setVariants(current => current.map((item, currentIndex) => currentIndex === index
        ? { ...item, images: (item.images || []).filter(image => image.id !== imageId) }
        : item));
      setSuccess('Variant image removed successfully.');
    } catch (err) {
      console.error('Error removing variant image:', err);
      setError(err.response?.data?.error || 'Failed to remove variant image.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (isCreateMode) {
        if (!formData.name.trim()) {
          setError('Product name is required.');
          return;
        }
        const typeMap = { Packet: 'packet', Loose: 'loose', Attribute: 'attribute' };
        const created = await createStockProduct({
          name: formData.name,
          category_id: formData.category_id,
          description: formData.description,
          type: typeMap[formData.type] || 'packet'
        });
        setSuccess('Product created. Opening the full editor...');
        navigate(`/products/edit/${created.id}`);
        return;
      }

      // Integer fields on product that should send 0 instead of ''
      const integerProductFields = new Set([
        'category_id',
        'brand_id',
        'tax_id',
        'status',
        'indicator',
        'is_unlimited_stock',
        'tax_included_in_price',
        'cod_allowed',
        'cancelable_status',
        'return_status',
        'return_days',
        'require_prescription',
        'total_allowed_quantity',
        'is_approved'
      ]);

      // Update product fields
      const productFields = ['name', 'category_id', 'brand_id', 'barcode', 'hsn', 'tax_id', 'status', 'type', 'indicator',
        'is_unlimited_stock', 'tax_included_in_price', 'cod_allowed', 'cancelable_status',
        'return_status', 'return_days', 'require_prescription', 'total_allowed_quantity', 'till_status', 'is_approved',
        'manufacturer', 'made_in', 'fssai_lic_no', 'tags', 'slug', 'meta_title', 'meta_keywords', 'meta_description', 'schema_markup', 'description', 'custom_field_values'];
      let successCount = 0;
      let failCount = 0;
      const failedFields = [];

      for (const variant of variants.filter(item => item.isNew)) {
        try {
          await createProductVariant(id, {
            ...variant,
            stock_unit_id: Number(variant.stock_unit_id) || 0,
            measurement: Number(variant.measurement) || 0,
            price: Number(variant.price) || 0,
            purchase_price: Number(variant.purchase_price) || 0,
            discounted_price: Number(variant.discounted_price) || 0,
            stock: Number(variant.stock) || 0,
            status: Number(variant.status) || 0
          });
          successCount++;
        } catch (e) {
          console.error('Failed creating variant', e);
          failedFields.push('New variant');
          failCount++;
        }
      }

      for (const field of productFields) {
        // Only update if value has changed from original loaded value
        if (formData[field] !== undefined && originalFormData && formData[field] !== originalFormData[field]) {
          let valueToSend = formData[field];

          // Normalize specific fields
          if (field === 'type') {
            // Backend accepts lowercase packet/loose; map to known values or blank
            const typeMap = { Packet: 'packet', Loose: 'loose', Attribute: 'attribute' };
            valueToSend = typeMap[valueToSend] || '';
          }

          if (integerProductFields.has(field)) {
            valueToSend = (valueToSend === '' || valueToSend === null) ? 0 : Number(valueToSend);
            if (Number.isNaN(valueToSend)) valueToSend = 0;
          }
          try {
            await updateProductField(id, field, valueToSend);
            successCount++;
          } catch (e) {
            console.error('Failed updating product field', field, e);
            failedFields.push(field);
            failCount++;
          }
        }
      }

      const translationFields = ['name', 'tags', 'manufacturer', 'description', 'meta_title', 'meta_keywords', 'schema_markup', 'meta_description'];
      for (const translation of translations) {
        const originalTranslation = originalTranslations.find(item => Number(item.language_id) === Number(translation.language_id)) || {};
        const changed = translationFields.some(field => (translation[field] || '') !== (originalTranslation[field] || ''));
        if (!changed) continue;
        try {
          const payload = Object.fromEntries(translationFields.map(field => [field, translation[field] || '']));
          await updateProductTranslation(id, translation.language_id, payload);
          successCount++;
        } catch (e) {
          console.error('Failed updating translation', translation.language_id, e);
          failedFields.push('Translation');
          failCount++;
        }
      }

      // Update variants
      for (const variant of variants) {
        const originalVariant = originalVariants.find(ov => ov.id === variant.id);
        if (!originalVariant) continue;

        if (JSON.stringify(variant.attribute_value_ids || []) !== JSON.stringify(originalVariant.attribute_value_ids || [])) {
          try {
            await updateVariantAttributes(variant.id, variant.attribute_value_ids || []);
            successCount++;
          } catch (e) {
            console.error('Failed updating variant attributes', variant.id, e);
            failedFields.push(`Variant ${variant.id} - attributes`);
            failCount++;
          }
        }

        const variantFields = ['price', 'purchase_price', 'discounted_price', 'stock', 'stock_unit_id', 'status', 'measurement', 'product_short_name', 'type'];
        for (const field of variantFields) {
          // Only update if the field has actually changed
          if (variant[field] === undefined || variant[field] === originalVariant[field]) {
            continue;
          }

          let v = variant[field];
          if (field === 'stock_unit_id' || field === 'status') {
            v = (v === '' || v === null) ? 0 : Number(v);
            if (Number.isNaN(v)) v = 0;
          } else if (field === 'price' || field === 'purchase_price' || field === 'discounted_price' || field === 'stock' || field === 'measurement') {
            v = (v === '' || v === null) ? 0 : Number(v);
            if (Number.isNaN(v)) v = 0;
          }
          try {
            await updateVariantField(variant.id, field, v);
            successCount++;
          } catch (e) {
            console.error('Failed updating variant field', variant.id, field, e);
            failedFields.push(`Variant ${variant.id} - ${field}`);
            failCount++;
          }
        }
      }

      if (failCount === 0) {
        setSuccess('Product updated successfully!');
        setTimeout(() => {
          navigate('/products');
        }, 1500);
      } else if (successCount > 0) {
        const failedList = failedFields.join(', ');
        setSuccess(`Changes saved, but some fields failed to update. Failed: ${failedList}. Please review.`);
      } else {
        setError('Failed to save product. Please try again.');
      }
    } catch (err) {
      console.error('Error saving product:', err);
      setError('Failed to save product. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async () => {
    setSaving(true);
    setError('');
    try {
      await restoreProduct(id);
      navigate('/products?preset=inactive-products');
    } catch (err) {
      console.error('Error restoring product:', err);
      setError(err.response?.data?.error || 'Failed to restore product.');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Archive this product? It will be removed from POS selection, while all invoice and order history is preserved.')) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      await archiveProduct(id);
      navigate('/products?preset=archived-products');
    } catch (err) {
      console.error('Error archiving product:', err);
      setError(err.response?.data?.error || 'Failed to archive product.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !product) {
    return (
      <Box sx={{ mt: 3, px: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/products')} sx={{ mt: 2 }}>
          Back to Products
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#f8f9fa',
      py: 3,
      px: { xs: 2, sm: 3, md: 4 }
    }}>
      {/* Modern Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/products')}
              size="small"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', transform: 'translateX(-2px)' },
                transition: 'all 0.2s'
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <EditIcon sx={{ fontSize: 28, color: 'white' }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', letterSpacing: '-0.5px' }}>
                  Edit Product
                </Typography>
                {product && (
                  <Chip 
                    label={`#${id}`} 
                    size="small" 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 22
                    }} 
                  />
                )}
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem' }}>
                {isCreateMode ? 'Create a product, then add its full catalog details' : 'Update product information and settings'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {!isCreateMode && !isArchived && (
              <Button variant="outlined" color="error" onClick={handleArchive} disabled={saving}>
                Archive Product
              </Button>
            )}
            {isArchived && (
              <Button variant="contained" onClick={handleRestore} disabled={saving} sx={{ bgcolor: '#991b1b', '&:hover': { bgcolor: '#7f1d1d' } }}>
                Restore Product
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => navigate('/products')}
              disabled={saving}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                '&:hover': { 
                  borderColor: 'rgba(255,255,255,0.5)',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                fontWeight: 600,
                px: 3,
                '&:hover': { bgcolor: '#f0f0f0' },
                boxShadow: '0 4px 14px 0 rgba(255,255,255,0.4)'
              }}
            >
              {saving ? 'Saving...' : isCreateMode ? 'Create Product' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            borderRadius: 2,
            border: '1px solid #ef5350',
            '& .MuiAlert-icon': { color: '#ef5350' }
          }}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 2, 
            borderRadius: 2,
            border: '1px solid #66bb6a',
            '& .MuiAlert-icon': { color: '#66bb6a' }
          }}
        >
          {success}
        </Alert>
      )}

      {/* Single Form Container */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3,
          borderRadius: 3,
          mb: 2,
          border: '1px solid #e0e0e0',
          bgcolor: 'white'
        }}
      >
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Product Image Section - Fixed Width */}
          <Box sx={{ 
            width: 220,
            flexShrink: 0,
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2,
            position: 'sticky',
            top: 20
          }}>
            {/* Image Card */}
            <Box sx={{ 
              p: 2,
              border: '2px dashed #e0e0e0',
              borderRadius: 2,
              bgcolor: '#fafafa',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5
            }}>
              <Avatar
                src={(() => {
                  const image = product?.image || product?.product_image || '';
                  if (image.startsWith('/uploads/') || image.startsWith('http://') || image.startsWith('https://')) return image;
                  return image ? `https://martadmin.owntrip.in/storage/${image}` : '';
                })()}
                alt={product?.name || product?.product_name}
                variant="rounded"
                sx={{ 
                  width: '100%',
                  height: 160,
                  bgcolor: 'white',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}
              >
                {!product?.image && !product?.product_image && (
                  <InventoryIcon sx={{ fontSize: 50, color: '#bdbdbd' }} />
                )}
              </Avatar>
              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                Product Image
              </Typography>
              <Button component="label" variant="outlined" size="small" disabled={uploadingImage}>
                {uploadingImage ? 'Uploading...' : 'Upload Image'}
                <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={handleImageUpload} />
              </Button>
              <Button component="label" variant="text" size="small" disabled={uploadingGallery}>
                {uploadingGallery ? 'Uploading gallery...' : 'Add Gallery Images'}
                <input hidden multiple type="file" accept="image/png,image/jpeg,image/webp" onChange={handleGalleryUpload} />
              </Button>
              {gallery.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75, width: '100%' }}>
                  {gallery.map(image => (
                    <Box key={image.id} sx={{ position: 'relative' }}>
                      <Avatar
                        variant="rounded"
                        src={image.image.startsWith('/uploads/') || image.image.startsWith('http') ? image.image : `https://martadmin.owntrip.in/storage/${image.image}`}
                        sx={{ width: '100%', height: 54, border: '1px solid #e0e0e0' }}
                      />
                      <IconButton
                        aria-label="Remove gallery image"
                        size="small"
                        onClick={() => handleGalleryRemove(image.id)}
                        sx={{ position: 'absolute', top: -9, right: -9, bgcolor: 'white', color: '#d32f2f', border: '1px solid #e0e0e0', '&:hover': { bgcolor: '#ffebee' } }}
                      >
                        <DeleteOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Status Card */}
            <Box sx={{ 
              p: 2,
              borderRadius: 2,
              bgcolor: formData.status === 1 ? '#e8f5e9' : '#fafafa',
              border: `1px solid ${formData.status === 1 ? '#81c784' : '#e0e0e0'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Status
              </Typography>
              <Chip 
                label={formData.status === 1 ? 'Active' : 'Inactive'}
                size="small"
                sx={{ 
                  bgcolor: formData.status === 1 ? '#4caf50' : '#9e9e9e',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 26,
                  px: 1
                }}
              />
            </Box>
          </Box>

          {/* Form Fields Container - Takes remaining space */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Basic Information */}
            <Box sx={{ mb: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2,
                  fontWeight: 700,
                  color: '#667eea',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&::before': {
                    content: '""',
                    display: 'block',
                    width: 4,
                    height: 18,
                    bgcolor: '#667eea',
                    borderRadius: 1
                  }
                }}
              >
                Basic Information
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(8, minmax(0, 1fr))'
                  }
                }}
              >
                <Box sx={{ gridColumn: { sm: 'span 2', md: 'span 4' } }}>
                  <TextField
                    fullWidth
                    label="Product Name"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleInputChange}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
                <Box sx={{ gridColumn: { md: 'span 2' } }}>
                  <TextField
                    fullWidth
                    label="Barcode"
                    name="barcode"
                    value={formData.barcode || ''}
                    onChange={handleInputChange}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
                <Box sx={{ gridColumn: { md: 'span 2' } }}>
                  <TextField
                    fullWidth
                    label="HSN"
                    name="hsn"
                    value={formData.hsn || ''}
                    onChange={handleInputChange}
                    size="small"
                  />
                </Box>
                <Box sx={{ gridColumn: { md: 'span 2' } }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      label="Status"
                      sx={{
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }}
                    >
                      <MenuItem value=""> </MenuItem>
                      <MenuItem value={1}>Active</MenuItem>
                      <MenuItem value={0}>Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ gridColumn: { md: 'span 2' } }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleInputChange}
                      label="Category"
                      sx={{
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }}
                    >
                      <MenuItem value="">—</MenuItem>
                      {categories.map(cat => (
                        <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ gridColumn: { md: 'span 2' } }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Brand</InputLabel>
                    <Select
                      name="brand_id"
                      value={formData.brand_id}
                      onChange={handleInputChange}
                      label="Brand"
                      sx={{
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }}
                    >
                      <MenuItem value="">—</MenuItem>
                      {brands.map(brand => (
                        <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ gridColumn: { md: 'span 1' } }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tax</InputLabel>
                    <Select
                      name="tax_id"
                      value={formData.tax_id}
                      onChange={handleInputChange}
                      label="Tax"
                      sx={{
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }}
                    >
                      <MenuItem value="">—</MenuItem>
                      {taxes.map(tax => (
                        <MenuItem key={tax.id} value={tax.id}>{tax.title} ({tax.percentage}%)</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ gridColumn: { md: 'span 1' } }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Product Type</InputLabel>
                    <Select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      label="Product Type"
                      sx={{
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }}
                    >
                      <MenuItem value="">—</MenuItem>
                      <MenuItem value={"Packet"}>Packet</MenuItem>
                      <MenuItem value={"Loose"}>Loose</MenuItem>
                      <MenuItem value={"Attribute"}>Attribute</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ gridColumn: { md: 'span 1' } }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Food Indicator</InputLabel>
                    <Select
                      name="indicator"
                      value={formData.indicator}
                      onChange={handleInputChange}
                      label="Food Indicator"
                      sx={{
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }}
                    >
                      <MenuItem value="">—</MenuItem>
                      <MenuItem value={0}>None</MenuItem>
                      <MenuItem value={1}>🟢 Veg</MenuItem>
                      <MenuItem value={2}>🔴 Non-Veg</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>

            {/* Product Settings - Below Basic Information */}
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2,
                  fontWeight: 700,
                  color: '#667eea',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&::before': {
                    content: '""',
                    display: 'block',
                    width: 4,
                    height: 18,
                    bgcolor: '#667eea',
                    borderRadius: 1
                  }
                }}
              >
                Product Settings
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))'
                  }
                }}
              >
                <Box>
                  <Box sx={{ 
                    px: 1.5,
                    py: 0.5,
                    height: 48,
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#667eea', boxShadow: '0 2px 8px rgba(102,126,234,0.1)' }
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Unlimited Stock
                    </Typography>
                    <Switch
                      checked={formData.is_unlimited_stock === 1}
                      onChange={(e) => handleInputChange({ target: { name: 'is_unlimited_stock', value: e.target.checked ? 1 : 0 } })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#667eea' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#667eea' }
                      }}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ px: 1.5, py: 0.5, height: 48, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>Prescription Required</Typography>
                    <Switch
                      checked={formData.require_prescription === 1}
                      onChange={(e) => handleInputChange({ target: { name: 'require_prescription', value: e.target.checked ? 1 : 0 } })}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ px: 1.5, py: 0.5, height: 48, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>Approved</Typography>
                    <Switch
                      checked={formData.is_approved === 1}
                      onChange={(e) => handleInputChange({ target: { name: 'is_approved', value: e.target.checked ? 1 : 0 } })}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ 
                    px: 1.5,
                    py: 0.5,
                    height: 48,
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#667eea', boxShadow: '0 2px 8px rgba(102,126,234,0.1)' }
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Tax Included
                    </Typography>
                    <Switch
                      checked={formData.tax_included_in_price === 1}
                      onChange={(e) => handleInputChange({ target: { name: 'tax_included_in_price', value: e.target.checked ? 1 : 0 } })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#667eea' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#667eea' }
                      }}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ 
                    px: 1.5,
                    py: 0.5,
                    height: 48,
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#667eea', boxShadow: '0 2px 8px rgba(102,126,234,0.1)' }
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      COD Allowed
                    </Typography>
                    <Switch
                      checked={formData.cod_allowed === 1}
                      onChange={(e) => handleInputChange({ target: { name: 'cod_allowed', value: e.target.checked ? 1 : 0 } })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#667eea' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#667eea' }
                      }}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ 
                    px: 1.5,
                    py: 0.5,
                    height: 48,
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#667eea', boxShadow: '0 2px 8px rgba(102,126,234,0.1)' }
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Cancelable
                    </Typography>
                    <Switch
                      checked={formData.cancelable_status === 1}
                      onChange={(e) => handleInputChange({ target: { name: 'cancelable_status', value: e.target.checked ? 1 : 0 } })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#667eea' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#667eea' }
                      }}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ 
                    px: 1.5,
                    py: 0.5,
                    height: 48,
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#667eea', boxShadow: '0 2px 8px rgba(102,126,234,0.1)' }
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Returnable
                    </Typography>
                    <Switch
                      checked={formData.return_status === 1}
                      onChange={(e) => handleInputChange({ target: { name: 'return_status', value: e.target.checked ? 1 : 0 } })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#667eea' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#667eea' }
                      }}
                    />
                  </Box>
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Return Days"
                    name="return_days"
                    type="number"
                    value={formData.return_days}
                    onChange={handleInputChange}
                    size="small"
                    disabled={formData.return_status === 0}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Product Variants Section */}
      {
        <Paper 
          elevation={0}
          sx={{ 
            p: 3,
            borderRadius: 3,
            border: '1px solid #e0e0e0',
            bgcolor: 'white',
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
            <Typography
              variant="h6"
              sx={{
              fontWeight: 700,
              color: '#667eea',
              fontSize: '1.1rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '&::before': {
                content: '""',
                display: 'block',
                width: 4,
                height: 20,
                bgcolor: '#667eea',
                borderRadius: 1
              }
            }}
            >
              Product Variants ({variants.length})
            </Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddVariant}>
              Add Variant
            </Button>
          </Box>
          <TableContainer sx={{ 
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#667eea', borderBottom: '2px solid #667eea', width: 330 }}>Short Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#667eea', borderBottom: '2px solid #667eea', width: 160 }}>Measurement</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#667eea', borderBottom: '2px solid #667eea', width: 160 }}>Unit</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#667eea', borderBottom: '2px solid #667eea', width: 120 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#667eea', borderBottom: '2px solid #667eea', minWidth: 220 }}>Attributes</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#667eea', borderBottom: '2px solid #667eea', width: 100 }} align="right">Price</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#667eea', borderBottom: '2px solid #667eea', width: 100 }} align="right">Purchase Cost</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#667eea', borderBottom: '2px solid #667eea', width: 100 }} align="right">Sale Price</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#667eea', borderBottom: '2px solid #667eea', width: 90 }} align="right">Stock</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#667eea', borderBottom: '2px solid #667eea', width: 120 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#667eea', borderBottom: '2px solid #667eea', minWidth: 130 }} align="center">Images</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#667eea', borderBottom: '2px solid #667eea', width: 64 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {variants.map((variant, index) => (
                  <TableRow 
                    key={variant.id}
                    sx={{ 
                      '&:hover': { bgcolor: '#f8f9fa' },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell sx={{ p: 1, width: 330 }}>
                      <TextField
                        value={variant.product_short_name}
                        onChange={(e) => handleVariantChange(index, 'product_short_name', e.target.value)}
                        size="small"
                        placeholder="Short Name"
                        sx={{
                          width: '315px',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#667eea' },
                            '&.Mui-focused fieldset': { borderColor: '#667eea' }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 1, width: 160 }}>
                      <TextField
                        type="number"
                        value={variant.measurement}
                        onChange={(e) => handleVariantChange(index, 'measurement', parseFloat(e.target.value))}
                        size="small"
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#667eea' },
                            '&.Mui-focused fieldset': { borderColor: '#667eea' }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 1, width: 160 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Unit</InputLabel>
                        <Select
                          label="Unit"
                          value={variant.stock_unit_id}
                          onChange={(e) => handleVariantChange(index, 'stock_unit_id', e.target.value)}
                          sx={{
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#667eea' },
                            '&.Mui-focused fieldset': { borderColor: '#667eea' }
                          }}
                        >
                          <MenuItem value="">—</MenuItem>
                          {units.map(unit => {
                            const label = unit.short_code || unit.short_name || unit.name || `Unit ${unit.id}`;
                            return (
                              <MenuItem key={unit.id} value={unit.id}>{label}</MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ p: 1, width: 120 }}>
                      <Select
                        value={variant.type}
                        onChange={(e) => handleVariantChange(index, 'type', e.target.value)}
                        size="small"
                        fullWidth
                      >
                        <MenuItem value="packet">Packet</MenuItem>
                        <MenuItem value="loose">Loose</MenuItem>
                        <MenuItem value="attribute">Attribute</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell sx={{ p: 1, minWidth: 220 }}>
                      <Select
                        multiple
                        value={variant.attribute_value_ids || []}
                        onChange={(e) => handleVariantChange(index, 'attribute_value_ids', e.target.value)}
                        size="small"
                        fullWidth
                        displayEmpty
                        renderValue={(selected) => selected.length
                          ? attributeOptions.filter(option => selected.includes(option.value_id)).map(option => option.value).join(', ')
                          : 'No attributes'}
                      >
                        {attributeOptions.map(option => (
                          <MenuItem key={option.value_id} value={option.value_id}>
                            {option.name}: {option.value}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell align="right" sx={{ p: 1 }}>
                      <TextField
                        type="number"
                        value={variant.price}
                        onChange={(e) => handleVariantChange(index, 'price', parseFloat(e.target.value))}
                        size="small"
                        sx={{ 
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#667eea' },
                            '&.Mui-focused fieldset': { borderColor: '#667eea' }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ p: 1 }}>
                      <TextField
                        type="number"
                        value={variant.purchase_price}
                        onChange={(e) => handleVariantChange(index, 'purchase_price', parseFloat(e.target.value))}
                        size="small"
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ p: 1 }}>
                      <TextField
                        type="number"
                        value={variant.discounted_price}
                        onChange={(e) => handleVariantChange(index, 'discounted_price', parseFloat(e.target.value))}
                        size="small"
                        sx={{ 
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#667eea' },
                            '&.Mui-focused fieldset': { borderColor: '#667eea' },
                            '& input': { color: variant.discounted_price > 0 ? '#d32f2f' : 'inherit', fontWeight: variant.discounted_price > 0 ? 600 : 400 }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ p: 1 }}>
                      <TextField
                        type="number"
                        value={variant.stock}
                        onChange={(e) => handleVariantChange(index, 'stock', parseFloat(e.target.value))}
                        size="small"
                        sx={{ 
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#667eea' },
                            '&.Mui-focused fieldset': { borderColor: '#667eea' },
                            '& input': { fontWeight: 600, color: '#424242' }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ p: 1 }}>
                      <Select
                        value={variant.status}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleVariantChange(index, 'status', val === '' ? '' : Number(val));
                        }}
                        size="small"
                        fullWidth
                        sx={{
                          '& fieldset': { borderColor: '#e0e0e0' },
                          '&:hover fieldset': { borderColor: '#667eea' },
                          '&.Mui-focused fieldset': { borderColor: '#667eea' },
                          '& .MuiSelect-select': {
                            color: variant.status === 1 ? '#4caf50' : '#f44336',
                            fontWeight: 600
                          }
                        }}
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value={1}>✓ Available</MenuItem>
                        <MenuItem value={0}>✕ Sold Out</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell align="center" sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                        {(variant.images || []).map(image => (
                          <Box key={image.id} sx={{ position: 'relative' }}>
                            <Avatar variant="rounded" src={image.image} sx={{ width: 28, height: 28 }} />
                            <IconButton aria-label="Remove variant image" size="small" onClick={() => handleVariantImageRemove(index, image.id)} sx={{ position: 'absolute', top: -10, right: -10, p: 0.1, bgcolor: 'white', color: '#d32f2f' }}>
                              <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Box>
                        ))}
                        <Button component="label" size="small" disabled={variant.isNew} sx={{ minWidth: 0, px: 0.75 }}>
                          Add
                          <input hidden multiple type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => handleVariantImageUpload(index, event)} />
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ p: 1 }}>
                      <IconButton
                        aria-label={variant.isNew ? 'Remove new variant' : 'Retire variant'}
                        color="error"
                        onClick={() => handleRetireVariant(index)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      }

            {/* Section: Additional Details */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 3,
                borderRadius: 3,
                mb: 2,
                border: '1px solid #e0e0e0',
                bgcolor: 'white',
                width: '100%'
              }}
            >
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2.5,
                  fontWeight: 700,
                  color: '#667eea',
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&::before': {
                    content: '""',
                    display: 'block',
                    width: 4,
                    height: 20,
                    bgcolor: '#667eea',
                    borderRadius: 1
                  }
                }}
              >
                Additional Details
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2.5,
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(4, minmax(0, 1fr))'
                  }
                }}
              >
                <Box>
                  <TextField
                    fullWidth
                    label="Manufacturer"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleInputChange}
                    size="small"
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Maximum Allowed Quantity"
                    name="total_allowed_quantity"
                    type="number"
                    value={formData.total_allowed_quantity}
                    onChange={handleInputChange}
                    size="small"
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Till Status"
                    name="till_status"
                    value={formData.till_status}
                    onChange={handleInputChange}
                    size="small"
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Made In"
                    name="made_in"
                    value={formData.made_in}
                    onChange={handleInputChange}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="FSSAI License No."
                    name="fssai_lic_no"
                    value={formData.fssai_lic_no}
                    onChange={handleInputChange}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Tags (comma separated)"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    size="small"
                    placeholder="e.g., organic, fresh, premium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Section: Descriptions */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2.5,
                  fontWeight: 700,
                  color: '#667eea',
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&::before': {
                    content: '""',
                    display: 'block',
                    width: 4,
                    height: 20,
                    bgcolor: '#667eea',
                    borderRadius: 1
                  }
                }}
              >
                Product Descriptions
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 2.5
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      '& .ql-toolbar': {
                        borderColor: '#e0e0e0',
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8
                      },
                      '& .ql-container': {
                        borderColor: '#e0e0e0',
                        borderBottomLeftRadius: 8,
                        borderBottomRightRadius: 8,
                        bgcolor: 'white'
                      },
                      '& .ql-editor': {
                        minHeight: 200,
                        fontSize: '0.95rem'
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                      Product Description
                    </Typography>
                    <ReactQuill
                      theme="snow"
                      value={formData.description}
                      onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                      modules={quillModules}
                      placeholder="Detailed description of the product..."
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {customFields.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#667eea', fontSize: '1.1rem' }}>
                  Custom Fields
                </Typography>
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
                  {customFields.map(field => {
                    const value = formData.custom_field_values?.[field.id] ?? '';
                    const options = Array.isArray(field.options) ? field.options : [];
                    if (field.type === 'file') {
                      return (
                        <Box key={field.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Button component="label" variant="outlined" size="small" disabled={uploadingCustomField === field.id}>
                            {uploadingCustomField === field.id ? 'Uploading...' : `Upload ${field.name}`}
                            <input hidden type="file" onChange={(event) => handleCustomFieldFileUpload(field.id, event)} />
                          </Button>
                          {value && <Button size="small" href={value} target="_blank" rel="noreferrer">View file</Button>}
                        </Box>
                      );
                    }
                    if (field.type === 'select' || field.type === 'dropdown' || field.type === 'radio') {
                      return (
                        <FormControl key={field.id} fullWidth size="small" required={field.is_required === 1}>
                          <InputLabel>{field.name}</InputLabel>
                          <Select label={field.name} value={value} onChange={(event) => handleCustomFieldChange(field.id, event.target.value)}>
                            <MenuItem value="">-</MenuItem>
                            {options.map(option => {
                              const optionValue = typeof option === 'object' ? (option.value ?? option.label ?? '') : option;
                              const optionLabel = typeof option === 'object' ? (option.label ?? option.value ?? '') : option;
                              return <MenuItem key={String(optionValue)} value={optionValue}>{optionLabel}</MenuItem>;
                            })}
                          </Select>
                        </FormControl>
                      );
                    }
                    if (field.type === 'checkbox' || field.type === 'boolean') {
                      return (
                        <Box key={field.id} sx={{ px: 1.5, height: 40, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 1 }}>
                          <Typography variant="body2">{field.name}</Typography>
                          <Switch checked={Boolean(value)} onChange={(event) => handleCustomFieldChange(field.id, event.target.checked)} />
                        </Box>
                      );
                    }
                    return <TextField key={field.id} fullWidth size="small" required={field.is_required === 1} label={field.name} type={field.type === 'number' ? 'number' : 'text'} multiline={field.type === 'textarea'} rows={field.type === 'textarea' ? 3 : undefined} value={value} onChange={(event) => handleCustomFieldChange(field.id, event.target.value)} />;
                  })}
                </Box>
              </Box>
            )}

            {languages.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#667eea', fontSize: '1.1rem' }}>
                  Translations
                </Typography>
                {languages.map(language => {
                  const translation = translationForLanguage(language.id);
                  const fields = [
                    ['name', 'Name'], ['manufacturer', 'Manufacturer'], ['tags', 'Tags'], ['meta_title', 'Meta Title'],
                    ['meta_keywords', 'Meta Keywords'], ['meta_description', 'Meta Description'], ['schema_markup', 'Schema Markup'], ['description', 'Description']
                  ];
                  return (
                    <Box key={language.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>{language.display_name}</Typography>
                      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
                        {fields.map(([field, label]) => <TextField key={field} fullWidth size="small" label={label} value={translation[field] || ''} multiline={['description', 'meta_description', 'schema_markup'].includes(field)} rows={['description', 'meta_description', 'schema_markup'].includes(field) ? 3 : undefined} onChange={(event) => handleTranslationChange(language.id, field, event.target.value)} />)}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Section: SEO Information */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2.5,
                  fontWeight: 700,
                  color: '#667eea',
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&::before': {
                    content: '""',
                    display: 'block',
                    width: 4,
                    height: 20,
                    bgcolor: '#667eea',
                    borderRadius: 1
                  }
                }}
              >
                SEO Information
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2.5,
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))'
                  }
                }}
              >
                <Box>
                  <TextField
                    fullWidth
                    label="URL Slug"
                    name="slug"
                    value={formData.slug}
                    size="small"
                    placeholder="product-url-slug"
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#f5f5f5',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#e0e0e0' },
                        '&.Mui-focused fieldset': { borderColor: '#e0e0e0' }
                      }
                    }}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Meta Title"
                    name="meta_title"
                    value={formData.meta_title}
                    onChange={handleInputChange}
                    size="small"
                    placeholder="SEO friendly title"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Meta Keywords"
                    name="meta_keywords"
                    value={formData.meta_keywords}
                    onChange={handleInputChange}
                    size="small"
                    placeholder="keyword1, keyword2, keyword3"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Meta Description"
                    name="meta_description"
                    value={formData.meta_description}
                    onChange={handleInputChange}
                    size="small"
                    multiline
                    rows={3}
                    placeholder="Short description for search engines..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
                <Box sx={{ gridColumn: { md: 'span 2' } }}>
                  <TextField
                    fullWidth
                    label="Schema Markup"
                    name="schema_markup"
                    value={formData.schema_markup}
                    onChange={handleInputChange}
                    size="small"
                    multiline
                    rows={5}
                    placeholder="JSON-LD structured data"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#667eea' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
            </Paper>

    </Box>
  );
};

export default ProductEdit;
